import {Observable, Subject} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import * as moment from 'moment';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    DocumentTypesEnum,
    DocumentStatusesEnum,
    IndividualDocumentInterface,
    LookupModel,
    IndividualDocumentModel,
    IndividualExtendedModel,
    CreateIndividualDocumentAndFilesInterface,
    IndividualDocumentsAtomicService,
    CreateIndividualDocumentAndFilesRequestModel,
    AuthApiService,
    ServerResponse,
    UpdateIndividualDocumentAndFilesInterface,
    UpdateIndividualDocumentAndFilesModel
} from '@spa/api';

import {InArrayValidatorKey, RequiredValidatorKey} from '@spa/validators';

import {Helper, SelectControl} from '../../../../shared/classes';

import {
    ProofOfAddressFormErrorStatesEnum
} from '../../enums';

import {ProofOfAddressControl, ProofOfAddressDocumentTypeSelector} from '../../../../../assets/descriptors';

export class ProofOfAddressForm {
    private proofOfAddressDocumentTypes: LookupModel;
    protected readonly authApiService: AuthApiService;
    protected readonly individualDocumentsAtomicService: IndividualDocumentsAtomicService;


    public individual: IndividualExtendedModel;
    public document: IndividualDocumentModel;
    public documentTypeControl: SelectControl;
    public documentSubject: Subject<string>;
    public documentErrorsSubject: Subject<string[]>;
    public documentFile: File;
    public reviewSummary: string;
    public hiddenDocument: boolean;

    constructor(
        proofOfAddressDocumentTypes: LookupModel,
        authApiService: AuthApiService,
        individualDocumentsAtomicService: IndividualDocumentsAtomicService
    ) {
        this.proofOfAddressDocumentTypes = proofOfAddressDocumentTypes;
        this.authApiService = authApiService;
        this.individualDocumentsAtomicService = individualDocumentsAtomicService;

        this.documentSubject = new Subject();
        this.documentErrorsSubject = new Subject();
        this.documentFile = null;
        this.document = null;
        this.reviewSummary = '';
        this.hiddenDocument = false;
    }

    private validateDocumentFile(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        const documentSubjectErrors = Helper.validateFile(this.documentFile, messageFunction);

        this.documentErrorsSubject.next(documentSubjectErrors);

        return (documentSubjectErrors.length === 0);
    }

    private validateControl(
        messageFunction: (controlLabel?: string) => { [key: string]: string; }
    ): boolean {
        const formsControlValue = this.documentTypeControl.value as string;

        if (!formsControlValue) {
            this.documentTypeControl.setError(messageFunction(this.documentTypeControl.placeholder)[RequiredValidatorKey]);

            return false;
        }

        const documentTypeIds = this.proofOfAddressDocumentTypes.getTypeKeys();

        if (documentTypeIds.indexOf(formsControlValue) < 0) {
            this.documentTypeControl.setError(messageFunction(this.documentTypeControl.placeholder)[InArrayValidatorKey]);

            return false;
        }

        return true;
    }

    private registerIndividualDocumentWithAtomicService(refreshDocument: boolean)
        : ServerResponse<IndividualDocumentModel> {

        const model: CreateIndividualDocumentAndFilesInterface = {
            individual_id: this.individual.id,
            document_type: DocumentTypesEnum.ProofOfAddress,
            sub_document_type: this.documentTypeControl.value as string,
            files: [this.documentFile],
            is_hidden_for_client: this.hiddenDocument,
            upload_stamp: moment().utc().toISOString(),
            uploaded_by: this.authApiService.getAuthenticatedUser().id,
            review_summary: this.reviewSummary,
            poi_document_id: null,
            file_name: ''
        };
        const createRequestModel = new CreateIndividualDocumentAndFilesRequestModel(model);

        return this.individualDocumentsAtomicService.AtomicCreateFile(createRequestModel)
            .pipe(
                switchMap((response: ApiResponse<ApiError[] | IndividualDocumentInterface>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponseObservable<IndividualDocumentInterface>(response,
                            ProofOfAddressFormErrorStatesEnum.UploadFileFailed);
                    }

                    const responseModel = (response.data as IndividualDocumentInterface);
                    this.document = new IndividualDocumentModel(responseModel);

                    this.individual.documents.push(this.document);

                    if (refreshDocument) {
                        this.documentSubject.next(this.document.fileStorageIds[0]);
                    }

                    this.documentFile = null;

                    return Helper.createSuccessResponseObservable<IndividualDocumentModel>(this.document);
                }));
    }

    private updateIndividualOwnDocumentRequest(refreshDocument: boolean): ServerResponse<IndividualDocumentModel> {
        const model = this.getIndividualDocumentAndFilesModelForUpdate();

        return this.individualDocumentsAtomicService.atomicUpdateFile(model).pipe(
            map((response: ApiResponse<ApiError[] | IndividualDocumentInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<IndividualDocumentInterface>(
                        response,
                        ProofOfAddressFormErrorStatesEnum.UpdateDocumentFailed
                    );
                }

                this.individual.documents.splice(this.individual.documents.findIndex(item => item.fileName === this.document.fileName
                    && item.documentType === this.document.documentType), 1);
                const data = response.data as IndividualDocumentInterface;
                const responseModel = new IndividualDocumentModel(data);
                this.document = responseModel;
                this.individual.documents.push(this.document);

                if (refreshDocument) {
                    this.documentSubject.next(this.document.fileStorageIds[0]);
                }
                this.documentFile = null;

                return Helper.createSuccessResponse<IndividualDocumentModel>(responseModel);
            }));
    }

    private getIndividualDocumentAndFilesModelForUpdate(): UpdateIndividualDocumentAndFilesModel {

        const ownDocumentUploadModelInterface: UpdateIndividualDocumentAndFilesInterface = {
            file_name: this.document.fileName,
            review_summary: this.document.reviewSummary,
            poi_document_id: this.document.poiDocumentId,
            is_hidden_for_client: this.document.isHiddenForClient,
            document_type: DocumentTypesEnum.ProofOfAddress,
            sub_document_type: this.documentTypeControl.value as string,
            files: [this.documentFile],
            uploaded_by: this.document.uploadedBy,
            upload_stamp: this.document.uploadStamp,
            id: this.document.id,
            modified_by: this.individual.userCorrelationId,
            modify_stamp: new Date().toUTCString(),
            file_storage_ids: this.document.fileStorageIds,
            individual_id : this.individual.id,
        };

        return new UpdateIndividualDocumentAndFilesModel(ownDocumentUploadModelInterface);

    }
    resetDocument(): void {
        this.document = null;
        this.documentSubject.next(null);
        this.documentErrorsSubject.next([]);
    }

    onDocumentUpload(file: File): void {
        this.documentFile = file;
    }

    initFormControl(value = null, placeholder = ''): void {
        this.documentTypeControl = ProofOfAddressControl();

        this.documentTypeControl.value = value;
        this.documentTypeControl.placeholder = !placeholder ? this.documentTypeControl.placeholder : placeholder;

        (this.documentTypeControl as SelectControl).options = this.proofOfAddressDocumentTypes.getTypeKeys()
            .map((key: string) => {
                return {
                    label: this.proofOfAddressDocumentTypes.getTypeValue(key),
                    value: key
                };
            });
    }

    initDocument(documents: IndividualDocumentModel[]): void {
        if (documents && documents.length > 0) {
            const waitingForApprovalStatuses =
                [DocumentStatusesEnum.New, DocumentStatusesEnum.InReview] as number[];

            const filteredDocuments = documents
                .filter((document: IndividualDocumentModel) => {
                    return (
                        (this.proofOfAddressDocumentTypes.getTypeKeys().indexOf(document.subDocumentType) >= 0) &&
                        (waitingForApprovalStatuses.indexOf(document.status) >= 0)
                    );
                })
                .sort((current: IndividualDocumentModel, next: IndividualDocumentModel) => {
                    return moment(next.uploadStamp).unix() - moment(current.uploadStamp).unix();
                });

            if (filteredDocuments[0]) {
                this.document = filteredDocuments[0];

                this.documentTypeControl.value = filteredDocuments[0].subDocumentType;
            }

            setTimeout(() => {
                if (filteredDocuments[0]) {
                    this.documentSubject.next(filteredDocuments[0].fileStorageIds[0]);
                }
            }, 0);
        }
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        return [
            this.validateDocumentFile(messageFunction),
            this.validateControl(messageFunction),
        ].indexOf(false) < 0;
    }

    getValidationErrors(): { [key: string]: string[] } {
        return {
            [ProofOfAddressDocumentTypeSelector]: this.documentTypeControl.errors
        };
    }

    createRequest(refreshDocument = true): Observable<ApiResponse<ApiError[] | IndividualDocumentModel>> | null {
        if (!this.document) {

            return this.registerIndividualDocumentWithAtomicService(refreshDocument);
        }

        if (
            this.document &&
            (this.document.fileStorageIds[0] !== this.documentFile.name)
        ) {
            this.updateIndividualOwnDocumentRequest(refreshDocument);
        }

        return null;
    }
}
